import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import api from '../services/api'; // Assuming api.ts is configured for auth calls
import { toast } from '../hooks/use-toast';
import SintisLogo from '../assets/Sintis_Logo.png';
import SintisIcon from '../assets/Sintis_Icon.png';
import LanguageSwitcher from '../components/LanguageSwitcher';
import SliderHumano from '../components/SliderHumano';

// Interface for company branding (can be expanded)
interface BrandingConfig {
  nomeEmpresa: string;
  logoUrl: string | null;
  corPrimaria: string;
}

// Default branding
const defaultBranding: BrandingConfig = {
  nomeEmpresa: 'SinTicket',
  logoUrl: null, // Or a default logo URL
  corPrimaria: '#25D366', // Default green
};

const Login: React.FC = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [branding, setBranding] = useState<BrandingConfig>(defaultBranding);
  const [verificacaoHumana, setVerificacaoHumana] = useState<string | null>(null);
  const [desafioHabilitado, setDesafioHabilitado] = useState(true);
  const [resetSlider, setResetSlider] = useState(0);
  const navigate = useNavigate();

  // TODO: Implement logic to fetch branding based on domain/subdomain or user input
  // useEffect(() => {
  //   const fetchBranding = async () => {
  //     try {
  //       // Example: Fetch based on hostname
  //       const hostname = window.location.hostname;
  //       const response = await api.get(`/api/branding?domain=${hostname}`);
  //       setBranding(response.data);
  //     } catch (err) {
  //       console.warn("Could not fetch custom branding, using default.");
  //       setBranding(defaultBranding);
  //     }
  //   };
  //   fetchBranding();
  // }, []);

  useEffect(() => {
    // Redirect if already logged in
    const token = localStorage.getItem('token');
    if (token) {
      // Only redirect if we have a valid token and no current error
      // Also check if we're not already on dashboard to prevent loops
      if (!error && window.location.pathname === '/login') {
        navigate('/dashboard');
      }
    }
  }, [navigate, error]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // The backend API needs to handle multitenant login
      // It should return user details including role and empresaId
      const response = await api.post('/usuarios/login', { email, senha, verificacaoHumana });

      if (response.data.token && response.data.usuario) {
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.usuario)); // Ensure user includes role, empresaId
        // Optionally store branding colors if needed globally
        // localStorage.setItem('branding', JSON.stringify(branding));

        toast({ title: t('login.loginSuccessTitle'), description: t('login.loginSuccessDesc') });
        if (response.data.usuario.role === 'SuperAdmin') {
          navigate('/configuracoes');
        } else {
          navigate('/dashboard');
        }
      } else {
        throw new Error(t('login.invalidResponse'));
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || t('login.defaultError');
      setError(errorMsg);
      // Verificação rejeitada/expirada: o slider precisa ser refeito
      if (err.response?.data?.codigo === 'VERIFICACAO_HUMANA') setResetSlider((n) => n + 1);
      toast({ title: t('login.loginErrorTitle'), description: errorMsg, variant: "destructive" });
      console.error("Login failed:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex items-center justify-center min-h-screen bg-gray-100 dark:bg-gray-900 p-4">
      <div className="absolute top-4 right-4 z-10">
        <LanguageSwitcher />
      </div>
      <div className="flex flex-col md:flex-row items-center justify-center w-full max-w-6xl bg-white dark:bg-gray-800 rounded-xl shadow-2xl overflow-hidden min-h-[70vh] md:min-h-[80vh] p-0 md:p-4">
        {/* Logo: ocupa topo no mobile, lateral no desktop */}
        <div className="w-full md:w-1/2 flex items-center justify-center bg-gray-50 dark:bg-gray-800 p-6 md:p-0">
          <img
            src={SintisLogo}
            alt="Sinticket Logo"
            className="object-contain w-40 h-28 md:w-full md:h-full md:p-8"
            style={{ maxHeight: '100%', maxWidth: '100%' }}
          />
        </div>
        {/* Card de login */}
        <div className="w-full md:w-1/2 flex items-center justify-center p-4 md:p-0">
          <Card className="w-full max-w-md shadow-lg dark:bg-gray-700">
            <CardHeader className="space-y-2 text-center">
              {branding.logoUrl ? (
                <img src={branding.logoUrl} alt={`${branding.nomeEmpresa} Logo`} className="w-24 h-auto mx-auto mb-4" />
              ) : (
                <img src={SintisIcon} alt={`Sinticket Logo`} className="w-24 h-auto mx-auto mb-4" />
              )}
              <CardTitle className="text-2xl font-bold">{branding.nomeEmpresa}</CardTitle>
              <CardDescription>{t('login.credentialsSubtitle')}</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="p-3 bg-red-100 border border-red-300 text-red-700 text-sm rounded-md dark:bg-red-900 dark:border-red-700 dark:text-red-200">
                    {error}
                  </div>
                )}
                <div className="space-y-2">
                  <label htmlFor="email" className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('login.email')}</label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="seu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="dark:bg-gray-700 dark:text-white dark:border-gray-600"
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label htmlFor="password" className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('login.password')}</label>
                    <Link
                      to="#"
                      className="text-sm hover:underline"
                      style={{ color: branding.corPrimaria }}
                      onClick={(e) => {
                        e.preventDefault();
                        toast({ title: t('login.forgotPasswordTitle'), description: t('login.forgotPasswordDesc') });
                      }}
                    >
                      {t('login.forgotPassword')}
                    </Link>
                  </div>
                  <div className="relative">
                    <Input
                      id="password"
                      type={mostrarSenha ? "text" : "password"}
                      placeholder="••••••••"
                      value={senha}
                      onChange={(e) => setSenha(e.target.value)}
                      required
                      className="dark:bg-gray-700 dark:text-white dark:border-gray-600 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setMostrarSenha(!mostrarSenha)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-300"
                      tabIndex={-1}
                    >
                      {mostrarSenha ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div className={desafioHabilitado ? '' : 'hidden'}>
                  <SliderHumano onChange={setVerificacaoHumana} onHabilitado={setDesafioHabilitado} resetKey={resetSlider} />
                </div>
                <Button
                  type="submit"
                  className="w-full text-white hover:opacity-90"
                  style={{ backgroundColor: branding.corPrimaria }}
                  disabled={loading || (desafioHabilitado && !verificacaoHumana)}
                >
                  {loading ? t('login.signingIn') : t('login.signIn')}
                </Button>
              </form>
              <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-600 text-center text-xs text-gray-500 dark:text-gray-400 space-x-3">
                <a href="/politica-de-privacidade" target="_blank" rel="noopener noreferrer" className="hover:underline">
                  {t('login.privacyPolicy')}
                </a>
                <span>|</span>
                <a href="/termos-de-uso" target="_blank" rel="noopener noreferrer" className="hover:underline">
                  {t('login.termsOfUse')}
                </a>
                <span>|</span>
                <a href="/data-deletion" target="_blank" rel="noopener noreferrer" className="hover:underline">
                  {t('login.dataDeletion')}
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Login;

